import React from 'react';

/**
 * SectionHeader — reusable section title block
 *
 * Props:
 *  badge       { icon: 'fas fa-*', text: string }  — optional orange pill badge above heading
 *  title       string | ReactNode                  — main heading (can include JSX spans)
 *  subtitle    string                              — optional description paragraph below heading
 *  align       'center' | 'left'                  — default 'center'
 *  mb          string                              — bottom margin class, default 'mb-16'
 *  titleSize   string                              — heading size classes, default 'text-4xl md:text-6xl'
 *
 * Usage:
 *  <SectionHeader
 *    badge={{ icon: 'fas fa-calendar-alt', text: 'Upcoming Sacred Events' }}
 *    title={<>Spiritual <span className="text-orange-500 font-bold">Events</span></>}
 *    subtitle="Join us for these special spiritual events..."
 *  />
 */
const SectionHeader = ({
  badge,
  title,
  subtitle,
  align = 'center',
  mb = 'mb-16',
  titleSize = 'text-4xl md:text-6xl',
}) => {
  const alignClass = align === 'left' ? 'text-left' : 'text-center';

  return (
    <div className={`${alignClass} ${mb}`}>
      {badge && (
        <div className="inline-flex items-center bg-white bg-opacity-80 backdrop-blur-sm rounded-full px-6 py-3 mb-6 shadow-md border border-orange-100">
          {badge.icon && <i className={`${badge.icon} text-orange-600 mr-3 text-lg`}></i>}
          <span className="text-orange-700 font-semibold">{badge.text}</span>
        </div>
      )}

      {title && (
        <h2 className={`${titleSize} font-bold text-gray-800 mb-6 leading-tight`}>
          {title}
        </h2>
      )}

      {subtitle && (
        <p className={`text-xl text-gray-600 leading-relaxed ${align === 'center' ? 'max-w-4xl mx-auto' : ''}`}>
          {subtitle}
        </p>
      )}
    </div>
  );
};

export default SectionHeader;
